import { JSX } from 'preact';
import { AsyncForm, FormGroup } from '@components/async-form';
import type { RuzcalFormQuestion, RuzcalFormQuestionAnswer } from 'client/cal/calendar-forms/types';
import { InputField } from './form-helpers';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { rpx } from 'client/lib/rpx-client';
import { useAsyncData } from 'client/lib/hooks';
import { showToast } from '@components/toaster';
import { validationError } from 'shared/utils';

export * from './form-helpers';

export type FormData = {
  id: string;
  name: string;
  description: string;
  questions: RuzcalFormQuestion[];
};

type FormattedResponse = {
  questionId: UUID;
  responses: {
    answerId: UUID;
    content: string;
  }[];
};

type Props = {
  form: FormData;
  userId?: string;
  eventId?: string;
  submissionId?: string;
  readonly: boolean;
  onSave?: () => void;
  onSkip?: () => void;
};

type FormSubmitValues = {
  [questionId: string]: string[];
};

interface AnswerItemProps extends JSX.InputHTMLAttributes<HTMLInputElement> {
  answer: RuzcalFormQuestionAnswer;
  response:
    | {
        answerId: UUID;
        content: string;
      }
    | undefined;
}

function AnswerItem(props: AnswerItemProps) {
  const { response, answer, checked, type, name, ...inputProps } = props;

  return (
    <div class="mt-4 rounded-sm relative cursor-pointer dark:text-gray-200">
      {type == 'text' && (
        <InputField
          name={`${name}`}
          placeholder={answer.title}
          inputWidth="w-96 max-w-full"
          checked={true}
          value={response?.content}
        />
      )}
      {(type == 'radio' || type == 'checkbox') && (
        <>
          <input
            {...inputProps}
            name={`${name}`}
            class="ml-1 mr-3 h-5 w-5 border-gray-500 dark:bg-gray-600 checked:bg-indigo-600 dark:checked:bg-sky-500"
            tabIndex={-1}
            checked={checked}
            value={answer.id}
            type={type}
          />
          <span>{answer.title}</span>
        </>
      )}
    </div>
  );
}
function QuestionItem({
  question,
  response,
  readonly,
}: {
  question: RuzcalFormQuestion;
  response: FormattedResponse | undefined;
  readonly: boolean;
}) {
  return (
    <div>
      <FormGroup prop={question.id}>
        <p class="whitespace-pre-line mb-4">
          {question.title || 'Question'}
          {question.isRequired && <span class="text-red-600 ml-2 text-lg">*</span>}
        </p>
        <div class="space-y-4">
          {question.answers.map((answer) => {
            return (
              <AnswerItem
                key={answer.id}
                name={`[]${question.id}`}
                answer={answer}
                type={question.type}
                checked={response?.responses.find((rr) => rr.answerId == answer.id) ? true : false}
                response={response?.responses.find((rr) => rr.answerId == answer.id)}
                disabled={readonly}
              />
            );
          })}
        </div>
      </FormGroup>
    </div>
  );
}

export function CalendarForm({ form, userId, eventId, readonly, onSave, onSkip }: Props) {
  const { questions } = form;

  const { data: responses } = useAsyncData(async () => {
    if (!eventId || !userId || !form.id) {
      return [];
    }

    const responses = await rpx.ruzcalForms.getFormUserEventResponses({
      formId: form.id,
      eventId: eventId,
      userId: userId,
    });

    const formattedResponses: FormattedResponse[] = [];
    responses.forEach((r) => {
      const q = formattedResponses.find((fr) => fr.questionId == r.questionId);
      if (q) {
        q.responses.push({
          answerId: r.answerId,
          content: r.content,
        });
      } else {
        // Push new question & answer
        formattedResponses.push({
          questionId: r.questionId,
          responses: [
            {
              answerId: r.answerId,
              content: r.content,
            },
          ],
        });
      }
    });
    return formattedResponses;
  }, []);

  async function submit(values: FormSubmitValues) {
    const missingRequired: string[] = [];

    questions.forEach((q) => {
      if (!q.isRequired) {
        return;
      }

      // If a form question is required and of type radio or check but, no responses have been submitted value will be undefined as there won't be a record of submitted fields
      const value = values[q.id];
      const isTextType = q.type === 'text';

      const isValidValue =
        Array.isArray(value) && (!isTextType || value.some((v: string) => v?.trim() !== ''));

      if (!isValidValue) {
        missingRequired.push(q.id);
      }
    });

    const errors = missingRequired.map((m) => ({
      field: m,
      message: 'This question is required',
    }));

    const answers = Object.keys(values).map((key) => {
      const q = questions?.find((e) => e.id == key);

      if (q?.type == 'text') {
        const formatted = values[key].map((a: string, idx: number) => {
          return {
            answerId: q.answers[idx].id,
            value: a,
          };
        });
        return {
          questionId: key,
          answers: formatted,
        };
      } else {
        const formatted = values[key].map((a: string) => {
          return {
            answerId: a,
            value: '',
          };
        });
        return {
          questionId: key,
          answers: formatted,
        };
      }
    });

    if (errors.length) {
      throw validationError(...errors);
    }

    try {
      if (eventId) {
        await rpx.ruzcalForms.saveCalendarFormResponses({
          formId: form.id,
          eventId: eventId,
          responses: answers,
        });
      }

      // Signal save
      if (onSave !== undefined) {
        onSave();
      }
    } catch (err) {
      showToast({
        title: 'Form error',
        type: 'warn',
        message: 'There was an error submitting your form responses, please try again.',
      });
    }
  }

  return (
    <AsyncForm class="" onSubmit={submit}>
      <header>
        <p class="mt-2">{form.description}</p>
      </header>
      <section class="mt-6 dark:text-gray-400">
        <div class="space-y-10">
          {questions.map((question) => (
            <QuestionItem
              key={question.id}
              question={question}
              response={responses?.find((r) => r.questionId == question.id)}
              readonly={readonly}
            />
          ))}
        </div>
        {!readonly && (
          <footer class="flex gap-4 pt-10">
            <BtnPrimary class="w-1/2 p-4 dark:bg-gray-900">Submit Form Responses</BtnPrimary>
            {onSkip && (
              <BtnSecondary class="w-1/2 p-4 dark:bg-gray-900" onClick={onSkip}>
                Skip for now
              </BtnSecondary>
            )}
          </footer>
        )}
      </section>
    </AsyncForm>
  );
}
